import React from "react";
import "../IntakeData/IntakeData.scss";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useState } from "react";
import Zoom from "react-medium-image-zoom";
import ReactPaginate from "react-paginate";
import "react-medium-image-zoom/dist/styles.css";
import Layout from "../Layouts/layout";
import { fetchData, SubmitData } from "../../../services/apiService";
import {
  decryptedData,
  encryptData,
} from "../../../services/encryptionDecription.service";
import ProfilePic from "../../../assets/img/ProfilePic2.png";
import useAlertUtils from "../../../utils/common.utils";

function IntakeData() {
  const [dbData, setdbData] = useState("");
  const [medicalHistoryStatus, setMedicalHistoryStatus] = useState({});
  const [isCheckingMedicalHistory, setIsCheckingMedicalHistory] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [sendingEmailMap, setSendingEmailMap] = useState({});
  // const [loader, setLoader] = useState(false);
  // const [pageLoader, setPageLoader] = useState(true);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  // Tabs
  // const [value, setValue] = React.useState("1");
  const { alertSuccess, alertError } = useAlertUtils();

  const sendVerificationEmail = async (userId) => {
    try {
      setSendingEmailMap((m) => ({ ...m, [userId]: true }));
      const res = await SubmitData(`/api/users/send-email-for-verification-reminder`, { userId });
      if (res && (res.success === true || res.status === true)) {
        alertSuccess(res.message || "Verification email sent.");
        // Optimistically update the row so UI reflects latest send time instantly
        try {
          const nowIso = new Date().toISOString();
          setdbData((prev) =>
            (prev || []).map((u) =>
              u?.id === userId ? { ...u, verificationReminderSentDate: nowIso } : u
            )
          );
        } catch (_) {}
      } else {
        alertError((res && res.message) || "Failed to send verification email.");
      }
    } catch (e) {
      alertError("Failed to send verification email.");
    } finally {
      setSendingEmailMap((m) => ({ ...m, [userId]: false }));
    }
  };

  // Function to check if all medical history APIs have data
  const checkMedicalHistoryStatus = async (userId) => {
    try {
      // The intakeForms endpoints expect the encrypted user id (same as used in Patient Details route)
      const encryptedUserId = btoa(encryptData(userId?.toString()))

      const [personalInfo, medicalHistory, questionnaires, documents] = await Promise.all([
        fetchData(`/api/intakeForms/pearsonalInformation?userId=${encryptedUserId}`),
        fetchData(`/api/intakeForms/medical/history?userId=${encryptedUserId}`),
        fetchData(`/api/intakeForms/questionnaires?userId=${encryptedUserId}`),
        fetchData(`/api/intakeForms/documents?userId=${encryptedUserId}`)
      ]);

      // More robust checking - check if data exists and has meaningful content
      const hasPersonalInfo = personalInfo?.success && personalInfo?.data && 
        Object.keys(personalInfo.data).length > 0 && 
        personalInfo.data.firstName; // Check for at least one meaningful field

      const hasMedicalHistory = medicalHistory?.success && medicalHistory?.data && 
        Object.keys(medicalHistory.data).length > 0 &&
        (medicalHistory.data.pastMedicalHistory || medicalHistory.data.currentMedications);

      const hasQuestionnaires = questionnaires?.success && questionnaires?.data && 
        Object.keys(questionnaires.data).length > 0 &&
        (questionnaires.data.weightLoss !== undefined || questionnaires.data.sleep !== undefined);

      const hasDocuments = documents?.success && documents?.data && 
        Object.keys(documents.data).length > 0 &&
        (documents.data.document_first || documents.data.selefie);

      const allFilled = hasPersonalInfo && hasMedicalHistory && hasQuestionnaires && hasDocuments;
      return allFilled;
    } catch (error) {
      return false;
    }
  };

  const handlePageClick = (selectedPage) => {
    const newPage = selectedPage?.selected ?? 0; // 0-based UI index
    setCurrentPage(newPage);
    dataFetch(newPage);
  };

  function formatUSPhoneNumber(phoneNumber) {
    const cleaned = phoneNumber.replace(/\D/g, ""); // Remove non-numeric characters

    if (cleaned?.length === 11) {
      const areaCode = cleaned?.substring(1, 4);
      const prefix = cleaned?.substring(4, 7);
      const lineNumber = cleaned?.substring(7);

      return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
    }

    // Return original if not a valid US phone number
    return phoneNumber;
  }

  function getYMD(inputDate) {
    if (!inputDate) return null;
    if (typeof inputDate === "string") {
      const m = inputDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
      }
    }
    const d = new Date(inputDate);
    if (isNaN(d.getTime())) return null;
    return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
  }

  function formatDate(inputDate) {
    const ymd = getYMD(inputDate);
    if (!ymd) return "Invalid Date";
    const month = (ymd.m + 1).toString().padStart(2, "0");
    const day = ymd.d.toString().padStart(2, "0");
    const year = ymd.y;
    return `${month}/${day}/${year}`;
  }

  function formatDateDMonY(inputDate) {
    const ymd = getYMD(inputDate);
    if (!ymd) return "-";
    const day = ymd.d.toString().padStart(2, "0");
    const months = [
      "jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"
    ];
    const mon = months[ymd.m];
    const year = ymd.y;
    return `${day}-${mon}-${year}`;
  }

  const dataFetch = async (page = 0) => {
    try {
      setIsUsersLoading(true);
      // Backend expects 1-based pages; UI uses 0-based indexes
      const apiPage = Number.isFinite(page) ? Number(page) + 1 : 1;
      let results = await fetchData(`/api/users/get-users?page=${apiPage}`);
      setTotalPage(results?.data?.totalPages);
      setdbData(results?.data?.users);
      
      // Check medical history status for each patient
      if (results?.data?.users) {
        setIsCheckingMedicalHistory(true);
        try {
          const statusPromises = results.data.users.map(async (user) => {
            const status = await checkMedicalHistoryStatus(user.id);
            return { userId: user.id, status };
          });
          
          const statuses = await Promise.all(statusPromises);
          const statusMap = {};
          statuses.forEach(({ userId, status }) => {
            statusMap[userId] = status;
          });
          setMedicalHistoryStatus(statusMap);
        } catch (error) {
        } finally {
          setIsCheckingMedicalHistory(false);
        }
      }
    } catch (err) {
      alertError("Error Fetching Information")
    } finally {
      setIsUsersLoading(false);
    }
  };

  useEffect(() => {
    dataFetch();
  }, []);

  return (
    <>
      <Layout>
        <div className="main_content intake_data">
          <div className="container">
            <div className="main_block">
              <div className="left_part">
                <div className="main_title mb-4">Intake Form Data</div>
              </div>

                  <div className="bottom_bar" style={{ width: '100%' }}>
                <div className="table_wrapper intake_form">
                  <div className="table_inner">
                    {isUsersLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px' }}>
                        <CircularProgress sx={{ color: '#e53935' }} />
                      </Box>
                    ) : (
                    <TableContainer component={Paper} sx={{ width: '100%' }}>
                      <Table size="small" sx={{ minWidth: 900, tableLayout: 'fixed' }} aria-label="patients table">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '0.85rem' }}>Patient</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>Name</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>Email</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>Verification</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>Medical History</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>Joined</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {dbData &&
                            dbData?.map((row, index) => (
                              <TableRow
                                key={uuidv4()}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                  <span className="mob_head">
                                    Patient Picture
                                  </span>
                                  <span className="text-capitalize">
                                    <Zoom>
                                      {" "}
                                      <picture>
                                        <img
                                          src={ProfilePic}
                                          alt="Profile Pic"
                                          className="productImage"
                                        />{" "}
                                      </picture>
                                    </Zoom>
                                  </span>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                  <span className="mob_head">Name</span>
                                  <Link
                                    to={`/app/patient-details/${btoa(
                                      encryptData(row?.id?.toString())
                                    )}`}
                                  >
                                    <span className="text-capitalize">
                                      {decryptedData(row?.firstName)}{" "}
                                      {decryptedData(row?.lastName)}
                                    </span>
                                  </Link>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>
                                  <span className="mob_head">Email</span>
                                  {decryptedData(row?.email) || ""}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                  <span className="mob_head">Status</span>
                                  <span className={`status-badge ${row?.verifyStatus === 1 ? 'verified' : 'not-verified'}`}>
                                    {row?.verifyStatus === 1 ? 'Verified' : 'Not Verified'}
                                  </span>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                  <span className="mob_head">Medical History</span>
                                  {isCheckingMedicalHistory ? (
                                    <span className="medical-history-badge loading">
                                      Checking...
                                    </span>
                                  ) : (
                                    <span className={`medical-history-badge ${medicalHistoryStatus[row?.id] ? 'filled' : 'not-filled'}`}>
                                      {medicalHistoryStatus[row?.id] ? 'Filled' : 'Not Filled'}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                  <span className="mob_head">
                                    Date Of Joining HRT
                                  </span>
                                  {formatDate(row?.createdAt)}
                                </TableCell>
                                <TableCell>
                                  <span className="mob_head">Action</span>
                                  {row?.verifyStatus !== 1 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                                      {(() => {
                                        const lastSentRaw = row?.verificationReminderSentDate;
                                        if (lastSentRaw) {
                                          return (
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                              Last email sent: {formatDateDMonY(lastSentRaw)}
                                            </span>
                                          );
                                        }
                                        return null;
                                      })()}
                                      <button
                                        onClick={() => sendVerificationEmail(row?.id)}
                                        disabled={Boolean(sendingEmailMap[row?.id])}
                                        style={{
                                          padding: '6px 10px',
                                          borderRadius: '8px',
                                          border: '1px solid #d1d5db',
                                          background: Boolean(sendingEmailMap[row?.id]) ? '#e5e7eb' : '#f9fafb',
                                          color: '#111827',
                                          fontWeight: 600,
                                          cursor: Boolean(sendingEmailMap[row?.id]) ? 'not-allowed' : 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}
                                      >
                                        {Boolean(sendingEmailMap[row?.id]) && (
                                          <CircularProgress size={14} sx={{ color: '#9f1239' }} />
                                        )}
                                        {Boolean(sendingEmailMap[row?.id])
                                          ? 'Sending...'
                                          : (row?.verificationReminderSentDate ? 'Send Email Again' : 'Send Verification Email')}
                                      </button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          {dbData?.length === 0 ? (
                            <TableRow
                              key={uuidv4()}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell colSpan={7}>
                                <div className=" notice">
                                  <div className="alert">
                                    <span className="alertText">
                                      No patient profiles found
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          ) : (
                            ""
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Pagination */}
            {dbData?.length !== 0 ? (
              <ReactPaginate
                breakLabel="..."
                nextLabel="next >"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={totalPage}
                forcePage={currentPage}
                previousLabel="< previous"
                renderOnZeroPageCount={null}
                marginPagesDisplayed={2}
                containerClassName="pagination justify-content-center"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                activeClassName="active"
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}

export default IntakeData;
